// SCR-CONSOLE-040 — تألیف و انتشار بسته تخصصی (US-CFG-030، 031): فهرست بسته‌ها و نسخه‌ها؛ ساخت خالی، کپی از بسته منتشرشده یا ورود از فایل
// JSON؛ ویرایش اقلام هر بخش (شناسه محلی، نشان نمونه، داده JSON) با اعتبارسنجی Schema ماژول مقصد در سرور (C-AGENT-687)؛ ارسال با یادداشت انتشار
// و انتشار با تأیید فردی غیر از تألیف‌کننده (BR-CFG-031). نسخه منتشرشده فقط‌خواندنی است.
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ManifestProblem,
  type PackSummaryView,
  type PackVersionView,
} from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  QueryState,
  SampleBadge,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { usePlatformAccess } from '../../access';
import { consoleKeys } from '../../api';

export const SCR_CONSOLE_040 = 'SCR-CONSOLE-040';

/** بخش‌های استاندارد به ترتیب اعمال (PackSections — Contracts CFG). */
export const PACK_SECTIONS = [
  'org.tags',
  'org.device_types',
  'catalog.categories',
  'doc.forms',
  'doc.consents',
  'doc.print_templates',
  'catalog.services',
  'cat.packages',
  'inv.items',
  'cln.note_templates',
  'cln.parameter_sets',
  'cln.contraindication_rules',
  'msg.templates',
  'msg.rules',
  'eng.followup_rules',
  'iam.roles',
] as const;

export interface ManifestItemJson {
  localId: string;
  isSample?: boolean;
  data: Record<string, unknown>;
}

export interface ManifestJson {
  code: string;
  name: string;
  description?: string;
  settings?: Record<string, string>;
  sections: Record<string, ManifestItemJson[]>;
}

const STATUS_TONE: Record<string, StatusTone> = { Draft: 'pending', PendingApproval: 'booked', Published: 'active' };

/** عنوان نمایشی قلم (نام، کد یا ارجاع کتابخانه). */
export function itemTitle(item: ManifestItemJson): string {
  const data = item.data ?? {};
  const definition = data['definition'] as Record<string, unknown> | undefined;
  const pick = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  return pick(data['name']) ?? pick(definition?.['name']) ?? pick(data['code']) ?? pick(data['library']) ?? item.localId;
}

function asManifest(value: unknown): ManifestJson {
  const manifest = (value ?? {}) as Partial<ManifestJson>;
  return {
    code: manifest.code ?? '',
    name: manifest.name ?? '',
    description: manifest.description,
    settings: manifest.settings ?? {},
    sections: manifest.sections ?? {},
  };
}

function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file, 'utf-8');
  });
}

function Problems({ problems }: { problems: readonly ManifestProblem[] }) {
  const { t } = useTranslation('console');
  if (problems.length === 0) {
    return null;
  }
  return (
    <Alert severity="error" data-testid="pack-problems">
      <AlertTitle>{t('console:packs.problems', { count: problems.length })}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {problems.slice(0, 30).map((problem, index) => (
          <li key={index}>
            <bdi dir="ltr">
              {[problem.section, problem.localId, problem.field].filter(Boolean).join(' › ')}: {problem.reason}
            </bdi>
          </li>
        ))}
      </Box>
    </Alert>
  );
}

type CreateMode = 'blank' | 'copy' | 'file';

function CreatePackDialog({ packs, onClose, onCreated }: { packs: readonly PackSummaryView[]; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const fileId = useId();
  const [mode, setMode] = useState<CreateMode>('blank');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [manifest, setManifest] = useState<ManifestJson | null>(null);
  const [fileProblem, setFileProblem] = useState(false);
  const published = packs.filter((pack) => pack.publishedVersion != null);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/configuration/console/specialty-packs', {
          body: {
            code: code.trim() || null,
            name: name.trim() || null,
            copyFromCode: mode === 'copy' ? copyFrom : null,
            copyFromVersion: null,
            manifest: mode === 'file' ? (manifest as unknown as Record<string, never>) : null,
          },
        }),
      ),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.packs });
      onCreated(view.id);
    },
  });

  const invalid = mode === 'file' ? !manifest : !code.trim() || !name.trim() || (mode === 'copy' && !copyFrom);

  return (
    <FormDialog
      open
      titleKey="console:packs.create.title"
      screenId={SCR_CONSOLE_040}
      onSubmit={() => !invalid && create.mutate()}
      onClose={onClose}
      isDirty={!!code || !!name || !!manifest}
      isSubmitting={create.isPending}
      submitError={create.error}
    >
      <TextField select label={t('console:packs.create.mode')} value={mode} onChange={(event) => setMode(event.target.value as CreateMode)} slotProps={{ htmlInput: { 'data-testid': 'pack-create-mode' } }}>
        <MenuItem value="blank">{t('console:packs.create.blank')}</MenuItem>
        <MenuItem value="copy">{t('console:packs.create.copy')}</MenuItem>
        <MenuItem value="file">{t('console:packs.create.file')}</MenuItem>
      </TextField>
      {mode === 'copy' && (
        <TextField select label={t('console:packs.create.copy_from')} value={copyFrom} onChange={(event) => setCopyFrom(event.target.value)} required>
          {published.map((pack) => (
            <MenuItem key={pack.code} value={pack.code}>
              {pack.name} ({faDigits(String(pack.publishedVersion))})
            </MenuItem>
          ))}
        </TextField>
      )}
      {mode === 'file' && (
        <Stack spacing={1}>
          <Button component="label" htmlFor={fileId} variant="outlined" startIcon={<UploadFileOutlinedIcon />}>
            {t('console:packs.create.choose_file')}
          </Button>
          <input
            id={fileId}
            type="file"
            accept=".json,application/json"
            hidden
            data-testid="pack-file"
            onChange={async (event) => {
              setFileProblem(false);
              setManifest(null);
              const file = event.target.files?.[0];
              if (!file) {
                return;
              }
              try {
                setManifest(asManifest(JSON.parse(await readText(file))));
              } catch {
                setFileProblem(true);
              }
            }}
          />
          {fileProblem && <Alert severity="error">{t('console:packs.create.file_invalid')}</Alert>}
          {manifest && (
            <Alert severity="info">
              {t('console:packs.create.file_loaded', { code: manifest.code, count: Object.values(manifest.sections).reduce((sum, items) => sum + items.length, 0) })}
            </Alert>
          )}
        </Stack>
      )}
      <TextField label={t('console:packs.code')} value={code} onChange={(event) => setCode(event.target.value)} required={mode !== 'file'} helperText={t('console:packs.code_hint')} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'pack-code' } }} />
      <TextField label={t('console:packs.name')} value={name} onChange={(event) => setName(event.target.value)} required={mode !== 'file'} slotProps={{ htmlInput: { 'data-testid': 'pack-name' } }} />
    </FormDialog>
  );
}

interface EditingItem {
  section: string;
  index: number | null;
  localId: string;
  isSample: boolean;
  data: string;
}

function ItemDialog({ item, onSave, onClose }: { item: EditingItem; onSave: (item: EditingItem) => void; onClose: () => void }) {
  const { t } = useTranslation('console');
  const [draft, setDraft] = useState(item);
  const parsed = useMemo(() => {
    try {
      const value: unknown = JSON.parse(draft.data);
      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    } catch {
      return null;
    }
  }, [draft.data]);
  const invalid = !/^[a-z0-9][a-z0-9._-]{1,63}$/.test(draft.localId) || parsed === null;
  return (
    <FormDialog
      open
      titleKey={item.index === null ? 'console:packs.item.add' : 'console:packs.item.edit'}
      screenId={SCR_CONSOLE_040}
      onSubmit={() => !invalid && onSave(draft)}
      onClose={onClose}
      isDirty={draft !== item}
      isSubmitting={false}
      maxWidth="md"
    >
      <TextField select label={t('console:packs.section')} value={draft.section} onChange={(event) => setDraft({ ...draft, section: event.target.value })} disabled={item.index !== null}>
        {PACK_SECTIONS.map((section) => (
          <MenuItem key={section} value={section}>
            {t(`console:packs.sections.${section}`, { defaultValue: section })}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t('console:packs.item.local_id')}
        value={draft.localId}
        onChange={(event) => setDraft({ ...draft, localId: event.target.value })}
        required
        helperText={t('console:packs.item.local_id_hint')}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'pack-item-local-id' } }}
      />
      <FormControlLabel control={<Checkbox checked={draft.isSample} onChange={(event) => setDraft({ ...draft, isSample: event.target.checked })} />} label={t('console:packs.item.is_sample')} />
      <TextField
        label={t('console:packs.item.data')}
        value={draft.data}
        onChange={(event) => setDraft({ ...draft, data: event.target.value })}
        multiline
        minRows={8}
        error={parsed === null}
        helperText={parsed === null ? t('console:packs.item.data_invalid') : t('console:packs.item.data_hint')}
        slotProps={{ htmlInput: { dir: 'ltr', style: { fontFamily: 'monospace' }, 'data-testid': 'pack-item-data' } }}
      />
    </FormDialog>
  );
}

function SubmitDialog({ version, onClose }: { version: PackVersionView; onClose: () => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const submit = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/configuration/console/specialty-pack-versions/{id}:submit', { params: { path: { id: version.id } }, body: { releaseNotes: notes } })),
    onSuccess: (view) => {
      queryClient.setQueryData(consoleKeys.packVersion(version.id), view);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.packs });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="console:packs.submit.title" screenId={SCR_CONSOLE_040} onSubmit={() => notes.trim() && submit.mutate()} onClose={onClose} isDirty={!!notes} isSubmitting={submit.isPending} submitError={submit.error}>
      <TextField label={t('console:packs.submit.notes')} value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={4} required helperText={t('console:packs.submit.notes_hint')} slotProps={{ htmlInput: { 'data-testid': 'pack-release-notes' } }} />
    </FormDialog>
  );
}

function VersionEditor({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EditingItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const version = useQuery({
    queryKey: consoleKeys.packVersion(id),
    queryFn: () => unwrap(api.GET('/api/v1/configuration/console/specialty-pack-versions/{id}', { params: { path: { id } } })),
  });
  const save = useMutation({
    mutationFn: ({ manifest, updatedAt }: { manifest: ManifestJson; updatedAt: string }) =>
      unwrap(
        api.PUT('/api/v1/configuration/console/specialty-pack-versions/{id}', {
          params: { path: { id } },
          body: { manifest: manifest as unknown as Record<string, never>, updatedAt },
        }),
      ),
    onSuccess: (view) => queryClient.setQueryData(consoleKeys.packVersion(id), view),
  });
  const decide = useMutation({
    mutationFn: (action: 'publish' | 'reject') =>
      unwrap(
        action === 'publish'
          ? api.POST('/api/v1/configuration/console/specialty-pack-versions/{id}:publish', { params: { path: { id } } })
          : api.POST('/api/v1/configuration/console/specialty-pack-versions/{id}:reject', { params: { path: { id } } }),
      ),
    onSuccess: (view) => {
      queryClient.setQueryData(consoleKeys.packVersion(id), view);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.packs });
    },
  });

  return (
    <QueryState isPending={version.isPending} error={version.error} data={version.data} onRetry={() => void version.refetch()} isEmpty={() => false}>
      {(view) => {
        const manifest = asManifest(view.manifest);
        const draft = view.status === 'Draft';
        const canPublish = access.can('platform.cfg.pack.publish') && view.status === 'PendingApproval';
        const update = (next: ManifestJson) => save.mutate({ manifest: next, updatedAt: view.updatedAt });
        const removeItem = (section: string, index: number) => {
          const items = [...(manifest.sections[section] ?? [])];
          items.splice(index, 1);
          update({ ...manifest, sections: { ...manifest.sections, [section]: items } });
        };
        const saveItem = (item: EditingItem) => {
          const items = [...(manifest.sections[item.section] ?? [])];
          const next: ManifestItemJson = { localId: item.localId.trim(), isSample: item.isSample, data: JSON.parse(item.data) as Record<string, unknown> };
          if (item.index === null) {
            items.push(next);
          } else {
            items[item.index] = next;
          }
          update({ ...manifest, sections: { ...manifest.sections, [item.section]: items } });
          setEditing(null);
        };
        const sections = Object.keys(manifest.sections).sort((a, b) => PACK_SECTIONS.indexOf(a as never) - PACK_SECTIONS.indexOf(b as never));

        return (
          <Stack spacing={2} data-testid="pack-editor">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button onClick={onBack}>{t('console:packs.back')}</Button>
              <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
                {manifest.name} — <bdi dir="ltr">{view.code}</bdi> · {t('console:packs.version', { version: faDigits(String(view.version)) })}
              </Typography>
              <StatusChip tone={STATUS_TONE[view.status] ?? 'inactive'} labelKey={`console:packs.status.${view.status}`} />
              {draft && (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing({ section: 'catalog.services', index: null, localId: '', isSample: false, data: '{\n}' })} data-testid="pack-add-item">
                  {t('console:packs.item.add')}
                </Button>
              )}
              {draft && (
                <Button variant="contained" onClick={() => setSubmitting(true)} disabled={view.problems.length > 0} data-testid="pack-submit">
                  {t('console:packs.submit.action')}
                </Button>
              )}
              {canPublish && (
                <>
                  <Button color="error" onClick={() => decide.mutate('reject')} disabled={decide.isPending} data-testid="pack-reject">
                    {t('console:packs.reject')}
                  </Button>
                  <Tooltip title={view.editedByMe ? t('console:packs.second_person_hint') : ''}>
                    <span>
                      <Button variant="contained" onClick={() => decide.mutate('publish')} disabled={view.editedByMe || decide.isPending} data-testid="pack-publish">
                        {t('console:packs.publish')}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
            {view.releaseNotes && (
              <Alert severity="info">
                <AlertTitle>{t('console:packs.submit.notes')}</AlertTitle>
                {view.releaseNotes}
              </Alert>
            )}
            {view.status === 'Published' && <Alert severity="success">{t('console:packs.published_hint')}</Alert>}
            {(save.error || decide.error) && <Alert severity="error">{t('console:packs.save_failed')}</Alert>}
            <Problems problems={view.problems} />
            {sections.map((section) => {
              const count = view.sections.find((candidate) => candidate.section === section);
              return (
                <Paper key={section} variant="outlined" sx={{ p: 1.5 }} data-testid={`pack-section-${section}`}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h4" component="h3" sx={{ flex: 1 }}>
                      {t(`console:packs.sections.${section}`, { defaultValue: section })} <bdi dir="ltr">({section})</bdi>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('console:packs.items_count', { count: count?.items ?? 0 })}
                    </Typography>
                    {count && !count.supported && <StatusChip tone="inactive" labelKey="console:packs.coming_soon" />}
                  </Stack>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {(manifest.sections[section] ?? []).map((item, index) => (
                      <Box component="li" key={item.localId + index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                        <bdi dir="ltr" style={{ minWidth: 140 }}>
                          {item.localId}
                        </bdi>
                        <span style={{ flex: 1 }}>{itemTitle(item)}</span>
                        {item.isSample && <SampleBadge />}
                        {draft && (
                          <>
                            <IconButton size="small" aria-label={t('console:packs.item.edit')} onClick={() => setEditing({ section, index, localId: item.localId, isSample: !!item.isSample, data: JSON.stringify(item.data, null, 2) })}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label={t('console:packs.item.delete')} onClick={() => removeItem(section, index)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              );
            })}
            {editing && <ItemDialog item={editing} onSave={saveItem} onClose={() => setEditing(null)} />}
            {submitting && <SubmitDialog version={view} onClose={() => setSubmitting(false)} />}
          </Stack>
        );
      }}
    </QueryState>
  );
}

export function SpecialtyPacksPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const packs = useQuery({ queryKey: consoleKeys.packs, queryFn: () => unwrap(api.GET('/api/v1/configuration/console/specialty-packs')) });
  const newDraft = useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/api/v1/configuration/console/specialty-packs/{code}/versions', { params: { path: { code } } })),
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.packs });
      setSelected(view.id);
    },
  });

  if (selected) {
    return (
      <ListPage screenId={SCR_CONSOLE_040} titleKey="console:packs.title">
        <VersionEditor id={selected} onBack={() => setSelected(null)} />
      </ListPage>
    );
  }

  const columns: DataTableColumn<PackSummaryView>[] = [
    { id: 'name', headerKey: 'console:packs.name', cell: (row) => <span>{row.name} <bdi dir="ltr">({row.code})</bdi></span> },
    { id: 'published', headerKey: 'console:packs.published_version', cell: (row) => (row.publishedVersion != null ? faDigits(String(row.publishedVersion)) : '—') },
    {
      id: 'latest',
      headerKey: 'console:packs.latest',
      cell: (row) => {
        const latest = row.versions[0];
        return latest ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{faDigits(String(latest.version))}</span>
            <StatusChip tone={STATUS_TONE[latest.status] ?? 'inactive'} labelKey={`console:packs.status.${latest.status}`} />
            <JalaliDate value={latest.updatedAt} withTime />
          </Stack>
        ) : null;
      },
    },
    {
      id: 'actions',
      headerKey: 'common:actions.title',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.versions[0] && (
            <Button size="small" onClick={() => setSelected(row.versions[0]!.id)} data-testid={`pack-open-${row.code}`}>
              {t('console:packs.open')}
            </Button>
          )}
          {row.versions[0]?.status === 'Published' && (
            <Button size="small" onClick={() => newDraft.mutate(row.code)} disabled={newDraft.isPending} data-testid={`pack-new-version-${row.code}`}>
              {t('console:packs.new_version')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_CONSOLE_040}
      titleKey="console:packs.title"
      subtitleKey="console:packs.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} data-testid="pack-create">
          {t('console:packs.create.action')}
        </Button>
      }
    >
      <DataTable columns={columns} rows={packs.data?.items} getRowId={(row) => row.code} isLoading={packs.isPending} error={packs.error} onRetry={() => void packs.refetch()} captionKey="console:packs.title" />
      {creating && <CreatePackDialog packs={packs.data?.items ?? []} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setSelected(id); }} />}
    </ListPage>
  );
}
